import React, { useState, useEffect } from "react";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { useTranslation } from "react-i18next";
import MeasurementDeleteModal from "../Modals/MeasurementDeleteModal";
import scansApi from "../../API/scans.api";
import { SuccessAlert } from "../../utils/Alerts";

const SimpleDataGrid = ({
    visible,
    data,
    selectedRows,
    setSelectedRows,
    scanId,
    afterDeleteReadings,
}) => {
    const [mixedData, setMixedData] = useState([]);
    const [gridApi, setGridApi] = useState(null);
    const [, setGridColumnApi] = useState(null);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (selectedRows && gridApi) {
            let nodes = [];

            // remove the unselected
            gridApi.getSelectedNodes().forEach((node) => {
                if (!selectedRows.includes(node.rowIndex)) {
                    node.setSelected(false);
                    nodes.push(node);
                }
            });

            // mark as selected
            for (const index of selectedRows) {
                const node = gridApi.getRowNode(index);
                if (node) {
                    node.setSelected(true);
                    nodes.push(node);
                }
            }

            gridApi.redrawRows({ rowNodes: nodes });
        }
    }, [selectedRows]);

    useEffect(() => {
        generateData(data);
    }, [data]);

    const generateData = (retrievedData) => {
        if (retrievedData && retrievedData.spectralData) {
            concatDataWithReference(retrievedData);
        }
    };

    const concatDataWithReference = (retrievedData) => {
        const modifiedData = [];
        for (const key in retrievedData.spectralData) {
            const spectral = retrievedData.spectralData[key];
            modifiedData.push([`${retrievedData.name} _${key}`, ...spectral]);
        }

        setMixedData(modifiedData);
        if (gridApi) {
            gridApi.setRowData(modifiedData);
            gridApi.redrawRows();
        }
    };

    const exportTable = () => {
        LogCapture.info("Single scan is exported successfully");
        gridApi.exportDataAsCsv({
            allColumns: true,
            skipColumnGroupHeaders: true,
            fileName: `${data.name}.csv`,
            processCellCallback: (params) => {
                if (params && params?.column?.colId === "capturedAt") {
                    return new Date(params?.value).toISOString();
                }
                return params?.value;
            },
            processHeaderCallback: (params) => {
                if (params && params?.column?.colId === "capturedAt") {
                    return "Captured At (UTC)";
                }
                return params?.column?.colDef?.headerName;
            },
        });
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
    };

    const onSelectionChanged = () => {
        const retrievedSelectedRows = gridApi
            .getSelectedNodes()
            .map((node) => node.rowIndex);
        setSelectedRows(retrievedSelectedRows);
    };

    const handleDelete = async () => {
        return new Promise((resolve, reject) => {
            if (gridApi) {
                try {
                    const count = selectedRows.length;
                    const readingKeys = selectedRows.map((rowIndex) => {
                        const scan = mixedData[rowIndex];
                        const lastIndex = scan[0].lastIndexOf("_");
                        return scan[0].slice(lastIndex + 1);
                    });
                    scansApi
                        .deleteScanReadings(scanId, readingKeys)
                        .then((res) => {
                            SuccessAlert(
                                t("deleteReadingsSuccessMessage", { count })
                            );
                            LogCapture.info(
                                `Scan readings deleted with count: ${count}`
                            );
                            setMixedData((prevState) =>
                                prevState.filter((item) => {
                                    const lastIndex = item[0].lastIndexOf("_");
                                    if (
                                        !readingKeys.includes(
                                            item[0].slice(lastIndex + 1)
                                        )
                                    )
                                        return item;
                                })
                            );
                            setSelectedRows([]);
                            afterDeleteReadings(res, scanId);
                            resolve();
                        });
                } catch (e) {
                    reject(e);
                }
            }
        });
    };

    return (
        <>
            <div
                className={`tabs-content-items flex flex-col mb-5 ${
                    visible ? "show" : ""
                }`}
                id="data-content-wrapper"
            >
                <div className="steps-btn flex items-center justify-end mb-4">
                    <div className="flex items-center">
                        <button
                            onClick={() => setDeleteModalVisible(true)}
                            className={`default-orange-outline-btn  ${
                                !selectedRows.length ? "disabled" : ""
                            }`}
                            disabled={!selectedRows.length}
                        >
                            <div className="flex items-center">
                                <i className="fas fa-trash-alt" />
                                <span className="ml-2 mr-1">{t("delete")}</span>
                                {selectedRows.length > 0 && (
                                    <span>({selectedRows.length})</span>
                                )}
                            </div>
                        </button>
                        <button
                            onClick={exportTable}
                            className="default-orange-btn"
                        >
                            <div className="flex items-center">
                                <i className="fas fa-file-download" />
                                <span className="ml-2">{t("export")}</span>
                            </div>
                        </button>
                    </div>
                </div>

                <div className="shadow sm:rounded-lg ag-grid-height">
                    <div
                        className="ag-theme-alpine text-left overflow-auto"
                        style={{ height: "100%" }}
                    >
                        <AgGridReact
                            onGridReady={onGridReady}
                            rowSelection="multiple"
                            defaultColDef={{
                                lockPosition: true,
                            }}
                            rowMultiSelectWithClick={true}
                            onSelectionChanged={onSelectionChanged}
                            rowData={mixedData}
                            // getRowNodeId={(data) => data[0]}
                        >
                            <AgGridColumn
                                field="0"
                                headerName="Sample Name"
                                checkboxSelection={true}
                                pinned={true}
                                width={200}
                                maxWidth={700}
                                resizable={true}
                                headerCheckboxSelection={true}
                                cellClass="text-gray-500"
                            ></AgGridColumn>

                            <AgGridColumn
                                field="data"
                                colId="data"
                                headerName="Spectral Data"
                            >
                                {data &&
                                    data.headers &&
                                    data.headers.map((col, index) => (
                                        <AgGridColumn
                                            key={index}
                                            field={(index + 1).toString()}
                                            headerName={
                                                isNaN(col)
                                                    ? col
                                                    : col.toString()
                                            }
                                        ></AgGridColumn>
                                    ))}
                            </AgGridColumn>
                        </AgGridReact>
                    </div>
                </div>
            </div>

            <MeasurementDeleteModal
                modalButton={deleteModalVisible}
                modalButtonSetter={setDeleteModalVisible}
                submitAction={handleDelete}
                title={t("deleteReadingsTitle")}
                message={`${t("deleteReadingsMessage", {
                    count: selectedRows.length,
                })}${
                    selectedRows.length === mixedData.length
                        ? ` ${t("deleteLastReading")}`
                        : ""
                }`}
            />
        </>
    );
};

export default SimpleDataGrid;
